import AdvancedCheckbox from '@portal/components/AdvancedCheckbox/AdvancedCheckbox';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import { onboarding } from '@portal/config/app.json';
import { translate } from '@portal/services/i18n';
import { maskCEP, unmaskField } from '@portal/services/masks';
import { Col, Row } from 'antd';
import React from 'react';
import {
  ADDRESS_MAXLENGTH_CITY,
  ADDRESS_MAXLENGTH_COMPLEMENT,
  ADDRESS_MAXLENGTH_NEIGHBORHOOD,
  ADDRESS_MAXLENGTH_NUMBER,
  ADDRESS_MAXLENGTH_PUBLIC_PLACE,
} from '~/config/env';

interface IProps {
  onFormChange: (key: string, value: string) => void;
  addressData?: models.Address;
  small?: boolean;
  addressError?: boolean;
}

const OnboardingAddress = ({
  onFormChange,
  addressData,
  small,
  addressError,
}: IProps) => {
  const handleNumberChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    onFormChange('addressNumber', numericValue);
  };

  return (
    <div className="onboarding-address">
      <Row gutter={16}>
        {!small && (
          <Col span={8}>
            <AdvancedInput
              placeholder={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.ZIP_CODE'
              )}
              variant="outlined"
              value={maskCEP(addressData?.zipCode ?? '')}
              onChange={(value) => {
                const cleanAddress = unmaskField(value);
                if (cleanAddress.length <= 8)
                  onFormChange('zipCode', cleanAddress);
              }}
              autoFocus
            />
          </Col>
        )}
        {small ? (
          <Col span={8}>
            <AdvancedInput
              label={translate(
                'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.ZIP_CODE'
              )}
              variant="outlined"
              value={maskCEP(addressData?.zipCode ?? '')}
              onChange={(value) => {
                const cleanAddress = unmaskField(value);
                if (cleanAddress.length <= 8)
                  onFormChange('zipCode', cleanAddress);
              }}
            />
          </Col>
        ) : null}
        <Col span={8}>
          <AdvancedInput
            variant="outlined"
            label={translate('PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.UF')}
            value={addressData?.state}
            readOnly={!addressError}
            maxLength={2}
            helperText={
              addressError
                ? translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.UF_HINT'
                  )
                : undefined
            }
            onChange={(value) =>
              onFormChange('state', value.toUpperCase().replace(/[^A-Z]/g, ''))
            }
          />
        </Col>
        <Col span={8}>
          <AdvancedInput
            helperText={
              addressError
                ? translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.CITY_HINT'
                  )
                : undefined
            }
            maxLength={
              onboarding.inputMaxLength ? ADDRESS_MAXLENGTH_CITY : undefined
            }
            variant="outlined"
            label={translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.CITY'
            )}
            value={addressData?.city}
            onChange={(value) => onFormChange('city', value)}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <AdvancedInput
            helperText
            maxLength={
              onboarding.inputMaxLength
                ? ADDRESS_MAXLENGTH_NEIGHBORHOOD
                : undefined
            }
            variant="outlined"
            label={translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.NEIGHBORHOOD'
            )}
            value={addressData?.neighborhood}
            onChange={(value) => onFormChange('neighborhood', value)}
          />
        </Col>
        <Col span={12}>
          <AdvancedInput
            helperText={
              addressError
                ? translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.STREET_HINT'
                  )
                : undefined
            }
            maxLength={
              onboarding.inputMaxLength
                ? ADDRESS_MAXLENGTH_PUBLIC_PLACE
                : undefined
            }
            variant="outlined"
            label={translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.PUBLIC_PLACE'
            )}
            value={addressData?.street}
            onChange={(value) => onFormChange('street', value)}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <AdvancedInput
            helperText={
              addressError
                ? translate(
                    'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.COMPLEMENT_HINT'
                  )
                : undefined
            }
            maxLength={ADDRESS_MAXLENGTH_COMPLEMENT}
            variant="outlined"
            label={translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.COMPLEMENT'
            )}
            value={addressData?.complement}
            onChange={(value) => onFormChange('complement', value)}
          />
        </Col>
        <Col span={12}>
          <AdvancedInput
            helperText
            maxLength={ADDRESS_MAXLENGTH_NUMBER}
            variant="outlined"
            label={translate(
              'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.NUMBER'
            )}
            value={addressData?.addressNumber}
            onChange={handleNumberChange}
            disabled={
              onboarding.checkboxAddressWithoutNumber
                ? addressData?.addressNumber === 'S/N'
                : false
            }
          />
          {onboarding.checkboxAddressWithoutNumber ? (
            <Row>
              <AdvancedCheckbox
                label={translate(
                  'PAGES.AUTH.REGISTER.ONBOARD.PF.ADDRESS.FORM.WITHOUT_NUMBER'
                )}
                value={addressData?.addressNumber === 'S/N'}
                onChange={(selected) =>
                  onFormChange('addressNumber', selected ? 'S/N' : '')
                }
              />
            </Row>
          ) : null}
        </Col>
      </Row>
    </div>
  );
};

export default OnboardingAddress;
